// ============================================================
// TMR Portal Admin — the Empresa's two statuses, and how each is worded
// ============================================================
import type { CompanyBusinessStatus, ExportStatus, RecordOrigin } from './contract';

/**
 * The badge key for each business status.
 *
 * They map onto keys the shared `STATUS_MAP` already carries rather than adding a parallel set, and the
 * indirection earns its keep twice: the backend values are PascalCase while badge keys are snake_case, and
 * `Rejected` already means "solicitação rejeitada" in that map. An Empresa is *reprovada*, which is a
 * different word for a different thing.
 */
export const BUSINESS_STATUS_BADGE: Record<CompanyBusinessStatus, string> = {
  PendingContract: 'pendente_contrato',
  PendingAccountTest: 'pendente_teste_conta',
  AwaitingSignature: 'aguardando_assinatura',
  Completed: 'concluido',
  Rejected: 'reprovado',
  Inactive: 'empresa_inativa',
};

/** Business statuses in lifecycle order, with the label the filter tabs and the status picker show. */
export const BUSINESS_STATUS_OPTIONS: { value: CompanyBusinessStatus; label: string }[] = [
  { value: 'PendingContract', label: 'Pendente contrato' },
  { value: 'PendingAccountTest', label: 'Pendente teste de conta' },
  { value: 'AwaitingSignature', label: 'Aguardando assinatura' },
  { value: 'Completed', label: 'Concluída' },
  { value: 'Rejected', label: 'Reprovada' },
  { value: 'Inactive', label: 'Inativa' },
];

/**
 * Statuses that stop the Empresa from operating. Today only one, but the screens ask the question ("can
 * this Empresa take a contract?") rather than comparing to a literal, so the next one is a single edit.
 */
export function canOperate(status: CompanyBusinessStatus): boolean {
  return status !== 'Inactive';
}

/**
 * How the Empresa stands between the two systems — the export status, with the one ambiguous case split by
 * where the record came from.
 *
 * Doubles as the badge key: the shared `STATUS_MAP` carries all four.
 */
export type SyncState = 'Pending' | 'Imported' | 'Exported' | 'Failed';

/**
 * Reads the two backend fields as the single fact the operator cares about: is this Empresa in step with the
 * legacy, and how did it get that way.
 *
 * Only `Exported` splits by origin. An imported Empresa whose edit is still on its way back to the legacy is
 * **Pendente**, not "Importada" — what matters at that moment is that the legacy copy is stale, and saying
 * "Importada" would hide the one thing the operator can act on.
 *
 * @param exportStatus Replication state, from `exportStatus`.
 * @param recordOrigin Where the record came from, from `recordOrigin`.
 */
export function syncState(exportStatus: ExportStatus, recordOrigin: RecordOrigin): SyncState {
  if (exportStatus !== 'Exported') return exportStatus;
  return recordOrigin === 'Imported' ? 'Imported' : 'Exported';
}

/** The sync filter's values. `all` is the unfiltered default and asks the server for nothing. */
export type SyncFilterValue = 'all' | 'pending' | 'imported' | 'exported' | 'failed';

/** One option of the sync filter, and the query it asks the server for. */
export interface SyncFilter {
  value: SyncFilterValue;
  label: string;
  exportStatus?: ExportStatus;
  origin?: RecordOrigin;
}

/**
 * The sync filter beside the search box.
 *
 * "Importadas" and "Exportadas" are the same export status told apart by origin, which is why this is a list
 * of query shapes and not a plain enum: the server does the filtering (paging and counts have to agree with
 * it), so each option has to carry both parameters.
 */
export const SYNC_FILTERS: SyncFilter[] = [
  { value: 'all', label: 'Todas' },
  { value: 'pending', label: 'Pendente', exportStatus: 'Pending' },
  { value: 'imported', label: 'Importadas', exportStatus: 'Exported', origin: 'Imported' },
  { value: 'exported', label: 'Exportadas', exportStatus: 'Exported', origin: 'Onboarded' },
  { value: 'failed', label: 'Com falha', exportStatus: 'Failed' },
];

/**
 * The query parameters for a sub-filter value, or an empty object for "Todas" and for anything unknown.
 *
 * @param value The selected sub-filter value.
 */
export function syncFilterQuery(value: string): { status?: ExportStatus; origin?: RecordOrigin } {
  const filter = SYNC_FILTERS.find((f) => f.value === value);
  if (!filter) return {};
  return { status: filter.exportStatus, origin: filter.origin };
}
